import React, { forwardRef, memo } from 'react';
import clsx from 'clsx';
import { groupBy } from 'lodash';
import t from 'utils/translationHelper';

import { AccountType } from '@alpha/currency-accounts-dtos';
import { InputLabel, MenuItem, Select } from '@alpha/ui-lib/ui/external';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Item } from './Item/Item';
import RenderValue from './RenderValue/RenderValue';
import { useStyles } from './Dropdown.styles';

export type TData = {
  id: string;
  friendlyName: string;
  balance: number;
  accountType: AccountType;
  currencyCode: string;
  externalAccount?: boolean;
  iban?: string;
  swift?: string;
  sortCode?: string;
  accountNumber?: string;
  reference?: string;
  clientAccountName: string;
};
interface IProps {
  data: TData[];
  helperText?: string | string[] | false;
  selectValue?: string;
  selectName?: string;
  placeholderText?: string;
  disabled?: boolean;
  hideBalance?: boolean;
  hideFlag?: boolean;
  testId?: string;
  handleChange?: (event: any) => void;
  handleBlur?: (event: any) => void;
  backgroundColor?: string;
  fx?: boolean;
}
const Dropdown: React.FC<IProps> = (props: IProps) => {
  const {
    data,
    selectValue,
    selectName,
    helperText,
    placeholderText,
    disabled,
    hideBalance,
    testId,
    handleChange,
    handleBlur,
    hideFlag,
    backgroundColor,
    fx,
  } = props;
  const styles = useStyles({ backgroundColor, fx });

  const dataGroupedByCurrency = groupBy(
    data,
    (dt: TData) => dt.currencyCode,
  );

  const dropdownItems = Object.keys(dataGroupedByCurrency).map(
    (currencyCode: string) => {
      const elements: JSX.Element[] = [
        <Item.Sticky currencyCode={currencyCode} />,
      ];
      const dataRows = dataGroupedByCurrency[currencyCode].map(
        (dropdownItem) => (
          <MenuItem
            key={dropdownItem.id}
            value={dropdownItem.id}
            style={{ padding: 0, backgroundColor: 'transparent' }}
          >
            <Item.Data item={dropdownItem} hideBalance={hideBalance} />
          </MenuItem>
        ),
      );
      return elements.concat(dataRows);
    },
  );

  const renderValue: Function | undefined = (
    values: string,
    _data: TData[],
  ) => {
    const selectedAccount = _data.find(
      (dt) => dt.id === values,
    );
    if (!selectedAccount) return undefined;
    return () => (
      <RenderValue selectedAccount={selectedAccount} hideBalance={hideBalance || selectedAccount.externalAccount} hideFlag={hideFlag} fx={fx} />
    );
  };

  const showValueOrPlaceHolder = selectValue || 'placeholder';
  const renderedPlaceHolderText = placeholderText || t('select_account');

  const CustomIcon = forwardRef((props, ref) => (
    <FontAwesomeIcon
      {...props}
      icon={faAngleDown}
      style={{
        width: '18px', height: '16px', margin: '0px 6px 0px 0px', color: 'rgba(52, 52, 52, 0.45)', right: 0,
      }}
      pointerEvents="none"
    />
  ));
  return (
    <div className={styles.dropdownContainer}>
      <Select
        IconComponent={
          CustomIcon
        }
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        value={showValueOrPlaceHolder}
        name={selectName}
        id={testId || 'dropdown'}
        renderValue={renderValue(showValueOrPlaceHolder, data)}
        disableUnderline
        fullWidth
        className={styles.dropdown}
        MenuProps={{
          className: styles.dropdownList,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
      >
        {[
          <MenuItem value="placeholder">
            <Typography variant="subtitle1" className={styles.placeholder}>
              {renderedPlaceHolderText}
            </Typography>
          </MenuItem>,
          ...dropdownItems,
        ]}
      </Select>
      <InputLabel className={clsx(styles.error, styles.red)}>
        {helperText}
      </InputLabel>
    </div>
  );
};

export default memo(Dropdown);
